import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react'
import { css } from 'src/ui/style/css'
import { useTheme } from 'src/ui/style/useTheme'
import { View } from 'src/ui/basic/View'
import Chat from './Chat'
import { useTran } from 'src/i18n/useTran'
import { msg } from '../../i18n/texts/keys'
import { isMobileScreen, ScreenSize } from '../style/ScreenSize'
import ChatbotButton from './ChatbotButton'

const { Sizes } = ScreenSize

interface ChatbotPopupProps {
    onSendMessage?: (message: string) => Promise<string>
    isOpen?: boolean
    onClose?: () => void
    onMinimize?: () => void
}

// Custom hook for managing body scroll lock on mobile

function useBodyScrollLock(lock: boolean): void {
    useLayoutEffect(() => {
        if (!lock) return

        const scrollY = window.scrollY
        const original = {
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width
        }

        // Freeze
        document.body.style.position = 'fixed'
        document.body.style.top = `-${scrollY}px`
        document.body.style.width = '100%' // prevent shrink on iOS Safari

        return () => {
            // Unfreeze – restore scroll exactly where the user was
            document.body.style.position = original.position
            document.body.style.top = original.top
            document.body.style.width = original.width
            window.scrollTo(0, scrollY)
        }
    }, [lock])
}

// Custom hook for managing popup state
const usePopupState = (isOpen: boolean, onClose?: () => void) => {
    const [isVisible, setIsVisible] = useState(isOpen)
    const [isMinimized, setIsMinimized] = useState(false)

    useEffect(() => {
        setIsVisible(isOpen)
        if (isOpen) {
            setIsMinimized(false)
        }
    }, [isOpen])

    const handleClose = useCallback(() => {
        setIsVisible(false)
        setIsMinimized(false)
        onClose?.()
    }, [onClose])

    const handleToggleMinimize = useCallback(() => {
        setIsMinimized(prev => !prev)
    }, [])

    return {
        isVisible,
        isMinimized,
        handleClose,
        handleToggleMinimize
    }
}

// Styles configuration
const createStyles = (theme: any, isMinimized: boolean) => ({
    container: css`
        position: fixed;
        bottom: 1.25rem;
        right: 1.25rem;
        z-index: 1000;
        width: ${isMinimized ? '12.5rem' : '21.875rem'};
        height: ${isMinimized ? 'auto' : '31.25rem'};
        ${isMinimized &&
        `
            background-color: ${theme.colors.secondary};
            border-radius: 3.125rem !important;
        `}
        transition: all 0.3s ease;
        border-radius: 0.75rem;
        overflow: hidden;
        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
        display: block;

        @media (max-width: ${Sizes.md}px) {
            position: fixed;
            z-index: 1100;
            width: 100%;
            height: 100%;
            bottom: 0;
            right: 0;
            left: 0;
            top: 0;
            border-radius: 0;
            ${isMinimized &&
            `
                width: auto;
                height: auto;
                background: transparent;
                box-shadow: none;
                overflow: visible;
                pointer-events: none;
            `}
        }

        /* Landscape on phones: height is small even though width > 768px */
        @media (orientation: landscape) and (max-height: ${Sizes.landscapeH}px) {
            position: fixed;
            z-index: 1100;
            width: 100%;
            height: 100%;
            bottom: 0;
            right: 0;
            left: 0;
            top: 0;
            border-radius: 0;
            ${isMinimized &&
            `
                width: auto;
                height: auto;
                background: transparent;
                box-shadow: none;
                overflow: visible;
                pointer-events: none;
            `}
        }
    `,

    header: css`
        padding: 0.9375rem;
        background-color: ${theme.colors.tertiary};
        color: ${theme.colors.white};
        font-weight: bold;
        display: flex;
        align-items: start;
        justify-content: space-between;
        cursor: pointer;
        position: relative;

        @media (max-width: ${Sizes.md}px) {
            justify-content: center;
            align-items: center;
            position: fixed;
            top: 0;
            left: 0;
            width: calc(100%);
            z-index: 1101;
            padding: 0.75rem 1rem;
            min-height: 3.5rem;
            padding-top: max(
                0.75rem,
                calc(env(safe-area-inset-top) + 0.75rem)
            );
            user-select: none;
            -webkit-user-select: none;
            touch-action: manipulation;
            box-sizing: border-box;
        }

        @media (max-width: ${Sizes.sm}px) {
            padding: 0.625rem 0.75rem;
            min-height: 3.25rem;
            padding-top: max(
                0.625rem,
                calc(env(safe-area-inset-top) + 0.625rem)
            );
        }

        @media (orientation: landscape) and (max-height: ${Sizes.landscapeH}px) {
            justify-content: center;
            align-items: center;
            position: fixed;
            top: 0;
            left: 0;
            width: calc(100%);
            z-index: 1101;
            padding: 0.625rem 1rem;
            min-height: 2.75rem;
            padding-top: max(
                0.625rem,
                calc(env(safe-area-inset-top) + 0.625rem)
            );
            user-select: none;
            -webkit-user-select: none;
            touch-action: manipulation;
            box-sizing: border-box;
        }

        ${isMinimized &&
        `
            background-color: ${theme.colors.secondary};
            border-radius: 3.125rem !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            width: 3.125rem;
            position: fixed;
            top: auto !important;
            left: auto !important;
            right: 1.25rem !important;
            bottom: 1.25rem !important;
            width: inherit !important;
            align-items: center;
            flex-direction: row;
            flex-wrap: nowrap;
            width: auto !important;
            padding: 0.75rem 1.25rem;
            gap: 0.5rem;
        `}
    `,

    title: css`
        font-size: 1rem;
        font-weight: 600;

        @media (max-width: ${Sizes.md}px) {
            font-size: 0.9375rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: calc(100% - 7rem);
            padding-right: 0.625rem;
        }

        @media (max-width: ${Sizes.sm}px) {
            font-size: 0.875rem;
            max-width: calc(100% - 6rem);
        }
    `,

    controls: css`
        position: ${isMinimized ? 'static' : 'absolute'};
        ${!isMinimized &&
        `
            top: 50%;
            right: 0.625rem;
            transform: translateY(-50%);
        `}
        display: flex;
        align-items: center;
        gap: 0.25rem;

        @media (max-width: ${Sizes.md}px) {
            ${!isMinimized &&
            `
                top: auto;
                right: 0.5rem;
                bottom: 0.25rem;
                transform: none;
            `}
            gap: 0;
        }

        @media (max-width: ${Sizes.sm}px) {
            ${!isMinimized &&
            `
                right: 0.375rem;
                bottom: 0.125rem;
            `}
            gap: 0;
        }
    `,

    headerButton: css`
        background: none;
        border: none;
        color: ${theme.colors.white};
        font-size: 1rem;
        cursor: pointer;
        padding: ${isMinimized ? '0' : '0.25rem 0 0'};
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;

        &:hover {
            opacity: 0.8;
        }

        @media (max-width: ${Sizes.md}px) {
            width: 2.5rem;
            height: 2.5rem;
            font-size: 1.125rem;
            min-width: 2.75rem;
            min-height: 2.75rem;

            @media (hover: none) {
                &:hover {
                    opacity: 1;
                    background: none;
                }

                &:active {
                    background: rgba(255, 255, 255, 0.2);
                }
            }
        }

        @media (max-width: ${Sizes.sm}px) {
            width: 2.25rem;
            height: 2.25rem;
            min-width: 2.5rem;
            min-height: 2.5rem;
            font-size: 1rem;
        }
    `,

    content: css`
        height: calc(100% - 3.125rem);
        width: 100%;
        overflow: hidden;
        display: ${isMinimized ? 'none' : 'block'};
    `
})

// Header component
interface ChatbotHeaderProps {
    title: string
    isMinimized: boolean
    onToggleMinimize: () => void
    onClose: () => void
    styles: ReturnType<typeof createStyles>
}

const ChatbotHeader: React.FC<ChatbotHeaderProps> = ({
    title,
    isMinimized,
    onToggleMinimize,
    onClose,
    styles
}) => {
    const isMobile = isMobileScreen()
    const handleButtonClick = useCallback(
        (e: React.MouseEvent, action: () => void) => {
            e.stopPropagation()
            action()
        },
        []
    )

    if (isMinimized && isMobile) {
        return <ChatbotButton onClick={onToggleMinimize} label={''} />
    }

    return (
        <View
            id="chatbot-header"
            css={styles.header}
            onClick={onToggleMinimize}
        >
            <span css={styles.title}>{title}</span>
            <View horizontal css={styles.controls}>
                {!isMinimized && (
                    <button
                        onClick={e => handleButtonClick(e, onToggleMinimize)}
                        css={styles.headerButton}
                        aria-label="Minimize chatbot"
                    >
                        −
                    </button>
                )}
                <button
                    onClick={e => handleButtonClick(e, onClose)}
                    css={styles.headerButton}
                    aria-label="Close chatbot"
                >
                    ✕
                </button>
            </View>
        </View>
    )
}

// Main component
const ChatbotPopup: React.FC<ChatbotPopupProps> = ({
    onSendMessage,
    isOpen = false,
    onClose
}) => {
    const t = useTran()
    const theme = useTheme()
    const isMobile = isMobileScreen()

    const { isVisible, isMinimized, handleClose, handleToggleMinimize } =
        usePopupState(isOpen, onClose)

    const shouldLockScroll = isMobile && isOpen && !isMinimized
    useBodyScrollLock(shouldLockScroll)

    const styles = createStyles(theme, isMinimized)

    if (!isVisible) {
        return null
    }

    return (
        <View id={'chatPopup'} css={styles.container}>
            <ChatbotHeader
                title={t(msg.chatbot.popup.title)}
                isMinimized={isMinimized}
                onToggleMinimize={handleToggleMinimize}
                onClose={handleClose}
                styles={styles}
            />

            <View css={styles.content}>
                <Chat onSendMessage={onSendMessage} />
            </View>
        </View>
    )
}

export default ChatbotPopup
